import { useEffect, useRef, useState } from 'react';
import type { AnalyticsPropertyType, AnalyticsSchemaDraftEvent } from '@voogzy/contracts';
import {
  cloneSchemaDocument,
  createEvent,
  createProperty,
  duplicateEvent,
  parseNullableInteger,
  slugify,
} from './schema-editor-helpers';
import type { SchemaEditorCommonProps, SchemaEventVersionState } from './schema-editor-types';
import { SchemaEditorActions, SchemaEmptySelection } from './SchemaEditorActions';

interface SchemaEventsEditorProps extends SchemaEditorCommonProps {
  eventVersions: SchemaEventVersionState[];
}

const propertyTypes: AnalyticsPropertyType[] = ['int64', 'double', 'bool', 'string', 'enum', 'json'];

export function SchemaEventsEditor(props: SchemaEventsEditorProps): React.JSX.Element {
  const [selectedId, setSelectedId] = useState(props.document.events[0]?.id ?? '');
  const [search, setSearch] = useState('');
  const draggedPropertyId = useRef<string | null>(null);

  useEffect(syncSelection, [props.document.events, selectedId]);

  const selected = findSelectedEvent();
  const version = selected === undefined ? undefined : findVersion(props.eventVersions, selected.id);
  const rows: React.JSX.Element[] = [];
  const normalizedSearch = search.trim().toLowerCase();
  for (const event of props.document.events) {
    if (normalizedSearch !== '' && !event.displayName.toLowerCase().includes(normalizedSearch) && !event.eventKey.includes(normalizedSearch)) {
      continue;
    }
    const category = findCategoryName(props, event.categoryId);
    const eventVersion = findVersion(props.eventVersions, event.id);
    rows.push(
      <button
        className={event.id === selectedId ? 'schema-list-row active' : 'schema-list-row'}
        data-id={event.id}
        key={event.id}
        onClick={selectEvent}
        type="button"
      >
        <span><strong>{event.displayName || 'Untitled event'}</strong><small>{category} · {event.eventKey || 'Missing key'}</small></span>
        <span><code>{event.eventCode ?? '—'}</code><small>v{eventVersion?.version ?? 1}</small></span>
      </button>,
    );
  }

  return (
    <section className="schema-editor-grid schema-event-editor-grid">
      <aside className="surface schema-definition-browser">
        <div className="surface-heading"><div><span className="eyebrow">Schema</span><h2>Events</h2></div><strong>{props.document.events.length}</strong></div>
        <input className="schema-search" placeholder="Search events" value={search} onChange={changeSearch} />
        {props.canManage && <button className="secondary-button schema-add-button" onClick={addEvent} type="button">Add event</button>}
        <div className="schema-list">{rows.length === 0 ? <p className="schema-empty-list">No events found.</p> : rows}</div>
      </aside>

      <article className="surface schema-detail-editor schema-event-editor">
        {selected === undefined ? <SchemaEmptySelection label="event" /> : (
          <>
            <div className="surface-heading">
              <div><span className="eyebrow">Event definition</span><h2>{selected.displayName || 'Untitled event'}</h2></div>
              <span className={version?.changed === true ? 'schema-version changed' : 'schema-version'}>v{version?.version ?? 1}{version?.changed === true ? ' next' : ''}</span>
            </div>
            <div className="schema-form-grid">
              <label>Display name<input name="displayName" value={selected.displayName} onChange={changeEventField} disabled={!props.canManage} /></label>
              <label>Technical key<input name="eventKey" value={selected.eventKey} onChange={changeEventField} disabled={!props.canManage} spellCheck={false} /></label>
              <label>Event code<input name="eventCode" type="number" min="1" max="4294967295" value={selected.eventCode ?? ''} onChange={changeEventField} disabled={!props.canManage} /></label>
              <label>Category<select name="categoryId" value={selected.categoryId ?? ''} onChange={changeEventField} disabled={!props.canManage}>{renderCategoryOptions(selected)}</select></label>
              <label className="schema-wide-field">Description<textarea name="description" value={selected.description} onChange={changeEventField} disabled={!props.canManage} /></label>
            </div>

            <div className="schema-subsection-heading">
              <div><span className="eyebrow">Ordered wire payload</span><h3>Properties</h3><p>Property order is part of the event contract. Drag rows or use the arrow controls to reorder them.</p></div>
              {props.canManage && <button className="secondary-button" onClick={addProperty} type="button">Add property</button>}
            </div>
            <div className="schema-properties">
              {renderProperties(selected)}
            </div>
            {props.canManage && (
              <SchemaEditorActions
                busy={props.busy}
                dirty={props.dirty}
                extra={<button className="secondary-button" onClick={copyEvent} type="button">Duplicate</button>}
                onDelete={deleteEvent}
                onReset={props.onReset}
                onSave={save}
              />
            )}
          </>
        )}
      </article>
    </section>
  );

  function syncSelection(): void {
    if (selectedId !== '' && findEventIndex(props, selectedId) >= 0) {
      return;
    }
    setSelectedId(props.document.events[0]?.id ?? '');
  }

  function findSelectedEvent(): AnalyticsSchemaDraftEvent | undefined {
    const index = findEventIndex(props, selectedId);
    return index < 0 ? undefined : props.document.events[index];
  }

  function renderCategoryOptions(event: AnalyticsSchemaDraftEvent): React.ReactNode {
    const options: React.JSX.Element[] = [];
    if (event.categoryId === null || findCategoryName(props, event.categoryId) === 'Missing category') {
      options.push(<option value="" key="missing">Select a category</option>);
    }
    for (const category of props.document.categories) {
      options.push(<option value={category.id} key={category.id}>{category.displayName || category.key || 'Untitled category'} · {category.wireCode ?? '—'}</option>);
    }
    return options;
  }

  function renderProperties(event: AnalyticsSchemaDraftEvent): React.ReactNode {
    if (event.properties.length === 0) {
      return <p className="schema-empty-list">This event has no properties. That is valid for marker-style events.</p>;
    }
    const rows: React.JSX.Element[] = [];
    for (let index = 0; index < event.properties.length; index += 1) {
      const property = event.properties[index]!;
      rows.push(
        <div
          className="schema-property-card"
          data-property-id={property.id}
          draggable={props.canManage}
          key={property.id}
          onDragOver={allowPropertyDrop}
          onDragStart={beginPropertyDrag}
          onDrop={dropProperty}
        >
          <div className="schema-property-order">
            <span className="schema-drag-handle" title="Drag to reorder">⋮⋮</span>
            <strong>{index + 1}</strong>
            {props.canManage && <span><button data-property-id={property.id} data-direction="up" onClick={moveProperty} disabled={index === 0} type="button">↑</button><button data-property-id={property.id} data-direction="down" onClick={moveProperty} disabled={index === event.properties.length - 1} type="button">↓</button></span>}
          </div>
          <div className="schema-property-fields">
            <label>Display name<input data-property-id={property.id} name="displayName" value={property.displayName} onChange={changePropertyField} disabled={!props.canManage} /></label>
            <label>Technical key<input data-property-id={property.id} name="key" value={property.key} onChange={changePropertyField} disabled={!props.canManage} spellCheck={false} /></label>
            <label>Type<select data-property-id={property.id} name="type" value={property.type} onChange={changePropertyField} disabled={!props.canManage}>{renderPropertyTypes()}</select></label>
            <label>Unit<input data-property-id={property.id} name="unit" value={property.unit ?? ''} onChange={changePropertyField} disabled={!props.canManage} placeholder="Optional" /></label>
            <label>Enum<select data-property-id={property.id} name="enumId" value={property.enumId ?? ''} onChange={changePropertyField} disabled={!props.canManage || property.type !== 'enum'}>{renderEnumOptions()}</select></label>
            <label>Common dimension<input data-property-id={property.id} name="commonDimension" value={property.commonDimension ?? ''} onChange={changePropertyField} disabled={!props.canManage} placeholder="e.g. level_id" /></label>
            <label className="schema-property-description">Description<input data-property-id={property.id} name="description" value={property.description} onChange={changePropertyField} disabled={!props.canManage} /></label>
            <label className="schema-checkbox-field"><input data-property-id={property.id} name="optional" type="checkbox" checked={property.optional} onChange={changePropertyField} disabled={!props.canManage} />Optional value</label>
          </div>
          {props.canManage && <button className="schema-row-delete" data-property-id={property.id} onClick={deleteProperty} type="button" aria-label={`Delete ${property.displayName}`}>×</button>}
        </div>,
      );
    }
    return rows;
  }

  function renderPropertyTypes(): React.ReactNode {
    const options: React.JSX.Element[] = [];
    for (const propertyType of propertyTypes) {
      options.push(<option value={propertyType} key={propertyType}>{propertyType}</option>);
    }
    return options;
  }

  function renderEnumOptions(): React.ReactNode {
    const options: React.JSX.Element[] = [<option value="" key="none">Select an enum</option>];
    for (const definition of props.document.enums) {
      options.push(<option value={definition.id} key={definition.id}>{definition.displayName || definition.key || 'Untitled enum'}</option>);
    }
    return options;
  }

  function changeSearch(event: React.ChangeEvent<HTMLInputElement>): void {
    setSearch(event.target.value);
  }

  function selectEvent(event: React.MouseEvent<HTMLButtonElement>): void {
    setSelectedId(event.currentTarget.dataset.id ?? '');
  }

  function addEvent(): void {
    const document = cloneSchemaDocument(props.document);
    const event = createEvent(document);
    document.events.push(event);
    props.onDocumentChange(document);
    setSelectedId(event.id);
  }

  function copyEvent(): void {
    const source = findSelectedEvent();
    if (source === undefined) {
      return;
    }
    const document = cloneSchemaDocument(props.document);
    const copy = duplicateEvent(document, source);
    document.events.push(copy);
    props.onDocumentChange(document);
    setSelectedId(copy.id);
  }

  function changeEventField(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void {
    const document = cloneSchemaDocument(props.document);
    const index = findEventIndex({ document }, selectedId);
    if (index < 0) {
      return;
    }
    const definition = document.events[index]!;
    const field = event.target.name;
    if (field === 'eventCode') {
      definition.eventCode = parseNullableInteger(event.target.value);
    } else if (field === 'displayName') {
      const priorSuggestedKey = slugify(definition.displayName);
      definition.displayName = event.target.value;
      if (definition.eventKey === priorSuggestedKey || definition.eventKey === '') {
        definition.eventKey = slugify(definition.displayName);
      }
    } else if (field === 'eventKey') {
      definition.eventKey = event.target.value;
    } else if (field === 'categoryId') {
      definition.categoryId = event.target.value === '' ? null : event.target.value;
    } else if (field === 'description') {
      definition.description = event.target.value;
    }
    props.onDocumentChange(document);
  }

  function addProperty(): void {
    const document = cloneSchemaDocument(props.document);
    const index = findEventIndex({ document }, selectedId);
    if (index < 0) {
      return;
    }
    document.events[index]!.properties.push(createProperty(document.events[index]!.properties));
    props.onDocumentChange(document);
  }

  function changePropertyField(event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void {
    const document = cloneSchemaDocument(props.document);
    const eventIndex = findEventIndex({ document }, selectedId);
    if (eventIndex < 0) {
      return;
    }
    const definition = document.events[eventIndex]!;
    const propertyIndex = findPropertyIndex(definition, event.currentTarget.dataset.propertyId ?? '');
    if (propertyIndex < 0) {
      return;
    }
    const property = definition.properties[propertyIndex]!;
    const field = event.target.name;
    if (field === 'displayName') {
      const priorSuggestedKey = slugify(property.displayName);
      property.displayName = event.target.value;
      if (property.key === priorSuggestedKey || property.key === '') {
        property.key = slugify(property.displayName);
      }
    } else if (field === 'key') {
      property.key = event.target.value;
    } else if (field === 'type') {
      property.type = event.target.value as AnalyticsPropertyType;
      property.enumId = property.type === 'enum' ? props.document.enums[0]?.id ?? null : null;
    } else if (field === 'unit') {
      property.unit = event.target.value === '' ? null : event.target.value;
    } else if (field === 'enumId') {
      property.enumId = event.target.value === '' ? null : event.target.value;
    } else if (field === 'commonDimension') {
      property.commonDimension = event.target.value === '' ? null : event.target.value;
    } else if (field === 'description') {
      property.description = event.target.value;
    } else if (field === 'optional' && event.target instanceof HTMLInputElement) {
      property.optional = event.target.checked;
    }
    props.onDocumentChange(document);
  }

  function deleteProperty(event: React.MouseEvent<HTMLButtonElement>): void {
    updatePropertyOrder(event.currentTarget.dataset.propertyId ?? '', 'delete');
  }

  function moveProperty(event: React.MouseEvent<HTMLButtonElement>): void {
    updatePropertyOrder(event.currentTarget.dataset.propertyId ?? '', event.currentTarget.dataset.direction ?? '');
  }

  function updatePropertyOrder(propertyId: string, operation: string): void {
    const document = cloneSchemaDocument(props.document);
    const eventIndex = findEventIndex({ document }, selectedId);
    if (eventIndex < 0) {
      return;
    }
    const properties = document.events[eventIndex]!.properties;
    const propertyIndex = findPropertyIndex(document.events[eventIndex]!, propertyId);
    if (propertyIndex < 0) {
      return;
    }
    if (operation === 'delete') {
      properties.splice(propertyIndex, 1);
    } else {
      const targetIndex = operation === 'up' ? propertyIndex - 1 : propertyIndex + 1;
      if (targetIndex < 0 || targetIndex >= properties.length) {
        return;
      }
      const moved = properties.splice(propertyIndex, 1)[0]!;
      properties.splice(targetIndex, 0, moved);
    }
    props.onDocumentChange(document);
  }

  function beginPropertyDrag(event: React.DragEvent<HTMLDivElement>): void {
    draggedPropertyId.current = event.currentTarget.dataset.propertyId ?? null;
    event.dataTransfer.effectAllowed = 'move';
  }

  function allowPropertyDrop(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  function dropProperty(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    const sourceId = draggedPropertyId.current;
    const targetId = event.currentTarget.dataset.propertyId ?? '';
    draggedPropertyId.current = null;
    if (sourceId === null || sourceId === targetId) {
      return;
    }
    const document = cloneSchemaDocument(props.document);
    const eventIndex = findEventIndex({ document }, selectedId);
    if (eventIndex < 0) {
      return;
    }
    const definition = document.events[eventIndex]!;
    const sourceIndex = findPropertyIndex(definition, sourceId);
    const targetIndex = findPropertyIndex(definition, targetId);
    if (sourceIndex < 0 || targetIndex < 0) {
      return;
    }
    const moved = definition.properties.splice(sourceIndex, 1)[0]!;
    definition.properties.splice(targetIndex, 0, moved);
    props.onDocumentChange(document);
  }

  function deleteEvent(): void {
    if (!window.confirm('Delete this event from the draft? It will be absent from the next published schema.')) {
      return;
    }
    const document = cloneSchemaDocument(props.document);
    const index = findEventIndex({ document }, selectedId);
    if (index < 0) {
      return;
    }
    document.events.splice(index, 1);
    props.onDocumentChange(document);
    setSelectedId(document.events[index]?.id ?? document.events[index - 1]?.id ?? '');
  }

  async function save(): Promise<void> {
    await props.onSave();
  }
}

function findEventIndex(props: Pick<SchemaEditorCommonProps, 'document'>, id: string): number {
  for (let index = 0; index < props.document.events.length; index += 1) {
    if (props.document.events[index]!.id === id) {
      return index;
    }
  }
  return -1;
}

function findPropertyIndex(event: AnalyticsSchemaDraftEvent, id: string): number {
  for (let index = 0; index < event.properties.length; index += 1) {
    if (event.properties[index]!.id === id) {
      return index;
    }
  }
  return -1;
}

function findVersion(versions: SchemaEventVersionState[], eventId: string): SchemaEventVersionState | undefined {
  for (const version of versions) {
    if (version.eventId === eventId) {
      return version;
    }
  }
  return undefined;
}

function findCategoryName(props: Pick<SchemaEditorCommonProps, 'document'>, categoryId: string | null): string {
  if (categoryId === null) {
    return 'Missing category';
  }
  for (const category of props.document.categories) {
    if (category.id === categoryId) {
      return category.displayName || category.key || 'Untitled category';
    }
  }
  return 'Missing category';
}
